/* eslint-disable react/jsx-props-no-spreading */
import { Controller } from "react-hook-form";
import type { UseFormMethods } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Stack, Typography, Grid } from "@mui/material";
import type { TextFieldProps } from "@mui/material";
import { format } from "date-fns";
import { IError } from "itsf-ui-common";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";
import { ResponsiveDatePicker } from "@common/ResponsiveDatePicker/ResponsiveDatePicker";
import { INoInstallActivationForm, useInstallationNoInstall } from "./useInstallationNoInstall";

export type { INoInstallActivationForm } from "./useInstallationNoInstall";

interface IParams {
    filteredActivationDateList: Date[];
    firstAvailableDate: Date;
    formMethods: UseFormMethods<INoInstallActivationForm>;
    isLoading: boolean;
    error?: IError;
    onRetryFetch?(): void;
}

export const InstallationNoInstall = (props: IParams) => {
    const { error, filteredActivationDateList, firstAvailableDate, formMethods, isLoading, onRetryFetch } = props;
    const { t } = useTranslation(["installation", "validation"]);

    const {
        activationDateRules,
        defaultMaxDate,
        defaultMinDate,
        displayError,
        emptyAvailability,
        shouldDisableDate,
    } = useInstallationNoInstall({ error, filteredActivationDateList, firstAvailableDate, isLoading });

    const { control, formState } = formMethods;
    const { errors } = formState;

    return (
        <Stack spacing={1}>
            <Typography color="secondary" variant="normal">
                {t("installation:dontNeedTechnicianToInstall")}
            </Typography>
            <Typography color="secondary" variant="normal">
                {t("installation:noInstallInfo")}
            </Typography>
            <HandleErrorAndLoading error={displayError} isLoading={isLoading} retryFetch={onRetryFetch}>
                {!emptyAvailability && error === undefined && (
                    <Grid container>
                        <Grid item md={6} xs={12}>
                            <Controller
                                control={control}
                                name="activationDate"
                                render={(controllerRenderProps) => (
                                    <ResponsiveDatePicker
                                        closeOnSelect
                                        inputFormat="dd/MM/yyyy"
                                        maxDate={defaultMaxDate}
                                        minDate={defaultMinDate}
                                        renderInput={(renderProps: TextFieldProps) => (
                                            <LabeledTextField
                                                {...renderProps}
                                                className="xtest_no_install_activation_date_input"
                                                error={Boolean(errors.activationDate)}
                                                fullWidth
                                                helperText={errors.activationDate?.message}
                                                id="noInstallActivationDate"
                                                inputProps={{
                                                    ...renderProps.inputProps,
                                                    "data-testid": "no-install-activation-date-input",
                                                }}
                                                inputRef={controllerRenderProps.ref}
                                                isRequired
                                                labelText={t("installation:activationDate")}
                                                name="noInstallActivationDate"
                                                placeholder={format(defaultMinDate, "dd/MM/yyyy")}
                                            />
                                        )}
                                        shouldDisableDate={shouldDisableDate}
                                        showToolbar={false}
                                        value={controllerRenderProps.value}
                                        onChange={controllerRenderProps.onChange}
                                    />
                                )}
                                rules={activationDateRules}
                            />
                        </Grid>
                    </Grid>
                )}
            </HandleErrorAndLoading>
        </Stack>
    );
};
